import { forwardRef, useEffect, useState } from "react";
import Modal, { CommonModalProps } from "..";
import {
    Stack,
    Button,
    Grid2 as Grid,
    FormControl,
    InputLabel,
    OutlinedInput,
    FormControlLabel,
    Switch,
    MenuItem,
    Select,
} from "@mui/material";
import FormSection from "../../Forms/FormSection/form-section";
import { setupService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { DbStorageInfos } from "../../../Core/Api/Types/db-storage-infos";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { DbFilePurgeRuleInfos } from "../../../Core/Api/Types/db-file-purge-rule-infos";
import { PurgeMethod } from "../../../Core/Api/Types/purge-method";
import { DbFormatInfos } from "../../../Core/Api/Types/db-format-infos";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { useTranslation } from "react-i18next";
import { GridRowsProp } from "@mui/x-data-grid";
import PurgeMetadataTable from "../../Table/PurgeMetadataTable/purge-metadata-table";

export type ModalPurgeRuleEditElementProps = {
    pr: DbFilePurgeRuleInfos;
    storages: DbStorageInfos[];
    isCreation: boolean;
    feedbackData: (pr: DbFilePurgeRuleInfos) => void;
};

export type ModalPurgeRuleEditProps = CommonModalProps<ModalPurgeRuleEditElementProps>;

const ModalPurgeRuleEdit = forwardRef<any, ModalPurgeRuleEditProps>((props, ref) => {
    let _rows: GridRowsProp = [];
    type Row = (typeof _rows)[number];
    const { t } = useTranslation();
    const { storages, isCreation, closeModal, feedbackData } = props;
    const [pr, setPR] = useState<DbFilePurgeRuleInfos>({
        ...props.pr,
        method: props.pr.method ?? PurgeMethod.UseDate,
        objectMetadata: props.pr.objectMetadata ? props.pr.objectMetadata.map((x, i) => ({ ...x, index: i })) : [],
    });
    const [formats, setFormats] = useState<DbFormatInfos[]>([]);
    const enqueueSnackbar = useCustomSnackbar();

    const handleCancelClick = () => {
        closeModal();
    };

    const handleConfirmClick = () => {
        //pr.objectMetadata?.forEach((e) => (e.valueLabel = "label"));
        console.log(pr.objectMetadata);
        (isCreation ? setupService.CreateFilePurgeRule(pr) : setupService.UpdateFilePurgeRule(pr)).then((data) => {
            if (!data.success || !data.objectId) {
                enqueueSnackbar(data.message!, "error");
                return;
            }
            feedbackData({ ...pr, id: data.objectId });
            enqueueSnackbar(pr.name!, "success");
            closeModal();
        });
    };

    useEffect(() => {
        setupService.GetFormats().then((data) => {
            if (data.formats) setFormats(data.formats ?? []);
        });

        if (isCreation) {
            setPR({
                platformId: -500,
                id: -500,
                isNew: true,
                name: "",
                enabled: true,
                method: PurgeMethod.UseDate,
                reachedDays: 15,
                deleteFileEntry: false,
                purgeOrphanFiles: false,
                passObjectToStatus: null,
                objectTypes: [],
                objectStatuses: [],
                objectMetadata: null,
                storageIds: [],
                formatIds: [],
                checkOnlineCopyOnStorageId: null,
                histories: [],
            });
        }
    }, [isCreation]);

    useEffect(() => {
        console.log(pr);
    }, [pr]);

    return (
        <Modal
            title={isCreation ? t("modal:title.purgeRuleCreation") : t("modal:title.purgeRuleEdit")}
            reference={ref}
            closeModal={handleCancelClick}
            width={800}
            footer={
                <Stack direction="row" justifyContent="space-between">
                    <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
                    <Button variant="contained" color="primary" onClick={handleConfirmClick} disabled={false}>
                        {t("common:save")}
                    </Button>
                </Stack>
            }
        >
            <FormSection title={t("common:generalInformationsLowerCase")} status="todo" defaultExpanded>
                <Grid container spacing={3} flexDirection={"row"}>
                    <Grid size={6}>
                        <FormControl sx={{ width: "100%" }}>
                            <InputLabel shrink htmlFor="name">
                                {t("common:name")}
                            </InputLabel>
                            <OutlinedInput
                                value={pr.name ?? ""}
                                id="name"
                                onChange={(e) => {
                                    setPR({ ...pr, name: e.target.value });
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    onChange={(e, checked) => {
                                        setPR({
                                            ...pr,
                                            enabled: checked,
                                        });
                                    }}
                                    checked={pr.enabled}
                                />
                            }
                            labelPlacement="start"
                            sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
                            label={t("field:label.isEnabledQuestionMark")}
                        />
                    </Grid>
                    <Grid size={6}>
                        <InputLabel sx={{ mb: 1 }}>{t("common:method")}</InputLabel>
                        <Select
                            value={pr?.method}
                            sx={{ width: "100%" }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                            fullWidth
                            onChange={(e) => {
                                setPR({
                                    ...pr,
                                    method: e.target.value ? PurgeMethod[e.target.value] : PurgeMethod.CreationDate,
                                });
                            }}
                        >
                            {Object.keys(PurgeMethod).map((type) => (
                                <MenuItem value={type} key={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>

                    <Grid size={6}>
                        <InputLabel htmlFor="maxfs" sx={{ mb: 1 }}>
                            {t("field:label.reachedDays")}
                        </InputLabel>
                        <OutlinedInput
                            id="days"
                            type={"number"}
                            value={pr.reachedDays}
                            fullWidth
                            onChange={(e) => {
                                setPR({
                                    ...pr,
                                    reachedDays: e.target.value ? parseInt(e.target.value) : 0,
                                });
                            }}
                        />
                    </Grid>
                    <Grid size={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    onChange={(e, checked) => {
                                        setPR({
                                            ...pr,
                                            deleteFileEntry: checked,
                                        });
                                    }}
                                    checked={pr.deleteFileEntry}
                                />
                            }
                            labelPlacement="start"
                            sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
                            label={t("field:label.deleteFileDBEntry")}
                        />
                    </Grid>
                    <Grid size={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    onChange={(e, checked) => {
                                        setPR({
                                            ...pr,
                                            purgeOrphanFiles: checked,
                                        });
                                    }}
                                    checked={pr.purgeOrphanFiles}
                                />
                            }
                            labelPlacement="start"
                            sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
                            label={t("field:label.deleteOrphanFiles")}
                        />
                    </Grid>

                    <Grid size={6}>
                        <InputLabel sx={{ mb: 1 }}>{t("field:label:changeStatusTo")}</InputLabel>
                        <Select
                            value={pr?.passObjectToStatus}
                            sx={{ width: "100%" }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                            fullWidth
                            onChange={(e) => {
                                setPR({
                                    ...pr,
                                    passObjectToStatus: e.target.value ? ObjectStatus[e.target.value] : null,
                                });
                            }}
                        >
                            {[ObjectStatus.Undefined, ObjectStatus.New, null].map((type) => (
                                <MenuItem value={type ?? undefined} key={type}>
                                    {type ?? "- Do not change -"}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>
                </Grid>
            </FormSection>
            <FormSection title={t("form:section.title.objectMatching")} status="todo" defaultExpanded sx={{ mt: 2 }}>
                <Grid container spacing={3} flexDirection={"row"}>
                    <Grid size={12}>
                        <FormAutocomplete
                            label={t("field:label.objectTypesToMatch")}
                            options={Object.keys(ObjectType)}
                            placeholder={t("field:placeholder.selectObjectType")}
                            value={pr?.objectTypes ?? []}
                            multiple
                            onChange={(e) => {
                                setPR({
                                    ...pr,
                                    objectTypes: e,
                                });
                            }}
                            getOptionLabel={(value: any) => value}
                            isOptionEqualToValue={(option, value) => option === value}
                            fullWidth
                            validationProps={{
                                formId: "formatvalidation",
                                formSectionId: "defs",
                                fieldId: "objtypes",
                                required: false,
                                initialState: false,
                            }}
                        />
                    </Grid>
                    <Grid size={12}>
                        <FormAutocomplete
                            label={t("field:label.objectStatusToMatch")}
                            options={Object.keys(ObjectStatus)}
                            placeholder={t("field:placeholder.selectObjectStatus")}
                            value={pr?.objectStatuses ?? []}
                            multiple
                            onChange={(e) => {
                                setPR({
                                    ...pr,
                                    objectStatuses: e,
                                });
                            }}
                            getOptionLabel={(value: any) => value}
                            isOptionEqualToValue={(option, value) => option === value}
                            fullWidth
                            validationProps={{
                                formId: "formatvalidation",
                                formSectionId: "defs",
                                fieldId: "objsts",
                                required: false,
                                initialState: false,
                            }}
                        />
                    </Grid>
                    <Grid size={12}>
                        <PurgeMetadataTable pr={pr} setPR={setPR} />
                    </Grid>
                </Grid>
            </FormSection>
            <FormSection title={t("form:section.title.storageConfiguration")} status="todo" defaultExpanded sx={{ mt: 2 }}>
                <Grid container spacing={3} flexDirection={"row"}>
                    <Grid size={12}>
                        <FormAutocomplete
                            label={t("field:label.storagesToPurge")}
                            options={storages}
                            placeholder={t("field:placeholder.selectObjectType")}
                            value={storages.filter((x) => pr.storageIds?.includes(x.id))}
                            multiple
                            onChange={(e) => {
                                setPR({
                                    ...pr,
                                    storageIds: e.map((x) => x.id),
                                });
                            }}
                            getOptionLabel={(value: any) => value.name}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            fullWidth
                            validationProps={{
                                formId: "formatvalidation",
                                formSectionId: "defs",
                                fieldId: "storages",
                                required: false,
                                initialState: false,
                            }}
                        />
                    </Grid>
                    <Grid size={12}>
                        <FormAutocomplete
                            label={t("field:label.formatsToMatch")}
                            options={formats}
                            placeholder={t("field:placeholder.selectFormats")}
                            value={formats.filter((x) => pr.formatIds?.includes(x.id))}
                            multiple
                            onChange={(e) => {
                                setPR({
                                    ...pr,
                                    formatIds: e.map((x) => x.id),
                                });
                            }}
                            getOptionLabel={(value: any) => value.name}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            fullWidth
                            validationProps={{
                                formId: "formatvalidation",
                                formSectionId: "defs",
                                fieldId: "formats",
                                required: false,
                                initialState: false,
                            }}
                        />
                    </Grid>
                    <Grid size={12}>
                        <FormAutocomplete
                            label={t("field:label.checkOnlineCopyOnStorage")}
                            options={storages}
                            placeholder={t("field:placeholder.selectAStorage")}
                            value={storages.find((x) => pr.checkOnlineCopyOnStorageId === x.id)}
                            onChange={(e) => {
                                setPR({
                                    ...pr,
                                    checkOnlineCopyOnStorageId: e ? e.id : null,
                                });
                            }}
                            getOptionLabel={(value: any) => value.name}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            fullWidth
                            validationProps={{
                                formId: "formatvalidation",
                                formSectionId: "defs",
                                fieldId: "chksts",
                                required: false,
                                initialState: false,
                            }}
                        />
                    </Grid>
                </Grid>
            </FormSection>
        </Modal>
    );
});

export default ModalPurgeRuleEdit;
